import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, DollarSign, Check, X, Eye, CreditCard, Package } from 'lucide-react';

const PaymentReportsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [toolsReports, setToolsReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('licenses');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadAllPayments();
  }, []);

  const loadAllPayments = async () => {
    setLoading(true);
    try {
      const [licensesRes, toolsRes] = await Promise.all([
        api.get('/api/superadmin/payment-requests?status=pending'),
        api.get('/api/superadmin/payment-reports/pending')
      ]);
      
      setLicenseRequests(licensesRes.data.requests || []);
      setToolsReports(toolsRes.data || []);
    } catch (error) {
      console.error('Error cargando pagos:', error);
      alert('Error al cargar pagos pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLicense = async (requestId) => {
    if (!confirm('¿Confirmas aprobar este pago de licencia?')) return;

    setProcessing(true);
    try {
      await api.put(`/api/superadmin/payment-requests/${requestId}/approve`);
      alert('✅ Pago aprobado');
      await loadAllPayments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveTools = async (reportId) => {
    if (!confirm('¿Confirmas aprobar este reporte?')) return;

    setProcessing(true);
    try {
      await api.post(`/api/payment-reports/${reportId}/approve`);
      alert('✅ Reporte aprobado');
      await loadAllPayments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) {
      alert('Debes especificar una razón');
      return;
    }

    setProcessing(true);
    try {
      const endpoint = selectedReport.request_type 
        ? `/api/superadmin/payment-requests/${selectedReport.id}/reject`
        : `/api/payment-reports/${selectedReport.id}/reject`;
      
      const method = selectedReport.request_type ? 'put' : 'post';
      await api[method](endpoint, { reason: rejectReason });
      
      alert('Rechazado');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedReport(null);
      await loadAllPayments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const totalPending = licenseRequests.length + toolsReports.length;

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className={`shadow-sm ${isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Reportes de Pago
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {totalPending} {totalPending === 1 ? 'pago pendiente' : 'pagos pendientes'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('licenses')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'licenses'
                ? 'border-b-2 border-blue-500 text-blue-500'
                : isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            📦 Pagos de Licencias ({licenseRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'tools'
                ? 'border-b-2 border-green-500 text-green-500'
                : isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            🛠️ Herramientas & Depósitos ({toolsReports.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {totalPending === 0 ? (
          <div className={`rounded-xl p-12 text-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No hay pagos pendientes
            </h2>
          </div>
        ) : (
          <>
            {/* Tab: Pagos de Licencias */}
            {activeTab === 'licenses' && (
              <div className="space-y-6">
                {licenseRequests.length === 0 ? (
                  <div className={`rounded-xl p-8 text-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      No hay pagos de licencias pendientes
                    </p>
                  </div>
                ) : (
                  licenseRequests.map((request) => (
                    <div key={request.id} className={`rounded-xl shadow-lg border-2 overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6">
                        <div className="flex items-center justify-between text-white">
                          <div>
                            <p className="text-sm opacity-90">
                              {request.request_type === 'first_payment' ? '🎉 Primer Pago' : 
                                     request.request_type === 'tools_purchase' ? '🛠️ Compra de Herramientas' : '🔄 Renovación'}
                            </p>
                            <p className="text-3xl font-bold">${request.payment_amount?.toFixed(2)}</p>
                            <p className="text-sm opacity-75 mt-1">
                              Plan: {request.billing_cycle === 'monthly' ? 'Mensual' : 
                                     request.billing_cycle === 'semiannual' ? 'Semestral' :
                                     request.billing_cycle === 'annual' ? 'Anual' : 'Pago Único'}
                            </p>
                          </div>
                          <CreditCard className="w-12 h-12 opacity-50" />
                        </div>
                      </div>

                      <div className="p-6 space-y-4">
                        <div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Usuario</p>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {request.user_email}
                          </p>
                        </div>

                        <div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Módulos solicitados</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {request.requested_modules?.map((mod, idx) => (
                              <span key={idx} className="px-2 py-1 rounded-md text-xs font-medium bg-blue-500 bg-opacity-20 text-blue-400">
                                {mod.toUpperCase()}
                              </span>
                            ))}
                          </div>
                        </div>

                        {request.payment_proof_url && (
                          <div>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Comprobante</p>
                            <button
                              onClick={() => {
                                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                                const publicUrl = request.payment_proof_url.replace('/app', '');
                                window.open(API_URL + publicUrl, '_blank');
                              }}
                              className="flex items-center gap-2 text-blue-500 hover:text-blue-600 text-sm font-medium mt-1"
                            >
                              <Eye className="w-4 h-4" />
                              Ver comprobante
                            </button>
                          </div>
                        )}

                        {request.payment_notes && (
                          <div>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Notas</p>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {request.payment_notes}
                            </p>
                          </div>
                        )}

                        <div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Fecha de solicitud</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {new Date(request.created_at).toLocaleString('es-MX')}
                          </p>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <button
                            onClick={() => handleApproveLicense(request.id)}
                            disabled={processing}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                          >
                            <Check className="w-5 h-5" />
                            Aprobar
                          </button>
                          <button
                            onClick={() => {
                              setSelectedReport(request);
                              setShowRejectModal(true);
                            }}
                            disabled={processing}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                          >
                            <X className="w-5 h-5" />
                            Rechazar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab: Herramientas & Depósitos */}
            {activeTab === 'tools' && (
              <div className="space-y-6">
                {toolsReports.length === 0 ? (
                  <div className={`rounded-xl p-8 text-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      No hay reportes pendientes
                    </p>
                  </div>
                ) : (
                  toolsReports.map((report) => (
                    <div key={report.id} className={`rounded-xl shadow-lg border-2 overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6">
                        <div className="flex items-center justify-between text-white">
                          <div>
                            <p className="text-sm opacity-90">Monto Reportado</p>
                            <p className="text-3xl font-bold">${report.amount?.toFixed(2) || 0}</p>
                          </div>
                          <DollarSign className="w-12 h-12 opacity-50" />
                        </div>
                      </div>

                      <div className="p-6 space-y-4">
                        <div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Usuario</p>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {report.user_name || report.seller_name || 'N/A'}
                          </p>
                          {report.user_email && (
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {report.user_email}
                            </p>
                          )}
                        </div>

                        <div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Referencia</p>
                          <p className={`font-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {report.reference || 'N/A'}
                          </p>
                        </div>

                        {(report.receipt_url || report.proof_url) && (
                          <div>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Comprobante</p>
                            <button
                              onClick={() => {
                                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                                const receiptPath = report.receipt_url || report.proof_url;
                                const publicUrl = receiptPath.replace('/app', '');
                                window.open(API_URL + publicUrl, '_blank');
                              }}
                              className="flex items-center gap-2 text-blue-500 hover:text-blue-600 text-sm font-medium mt-1"
                            >
                              <Eye className="w-4 h-4" />
                              Ver comprobante
                            </button>
                          </div>
                        )}

                        <div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Fecha de reporte</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {new Date(report.created_at).toLocaleString('es-MX')}
                          </p>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <button
                            onClick={() => handleApproveTools(report.id)}
                            disabled={processing}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                          >
                            <Check className="w-5 h-5" />
                            Aprobar
                          </button>
                          <button
                            onClick={() => {
                              setSelectedReport(report);
                              setShowRejectModal(true);
                            }}
                            disabled={processing}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                          >
                            <X className="w-5 h-5" />
                            Rechazar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Modal Rechazar */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 max-w-md w-full ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Rechazar Solicitud
            </h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Razón del rechazo..."
              rows={4}
              className={`w-full p-3 rounded-lg border mb-4 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setSelectedReport(null);
                }}
                className={`flex-1 py-2 rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg disabled:opacity-50"
              >
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReportsPage;
